import { fetchData, fetchCheckout } from '../utils/async';
import { addClass } from '../utils/dom';
import { deepClone } from '../utils/object';
import { fieldValidation, urlify } from '../utils/string';
import getVisitorConsent from '../utils/consent';

export const SUBSCRIBED = 'SUBSCRIBED';
export const SUBSCRIBED_ERROR = 'SUBSCRIBED_ERROR';
export const RECENTLY_VIEWED = 'RECENTLY_VIEWED';
export const ADD_VOUCHER = 'ADD_VOUCHER';
export const VOUCHER_INPUT = 'VOUCHER_INPUT';
export const VOUCHER_ERROR = 'VOUCHER_ERROR';
export const ADDED_TO_BAG = 'ADDED_TO_BAG';
export const ADD_TO_BAG_ERROR = 'ADD_TO_BAG_ERROR';
export const DISCOUNT_ERROR = 'DISCOUNT_ERROR';
export const SELECT_SIZE = 'SELECT_SIZE';
export const SELECT_QUANTITY = 'SELECT_QUANTITY';
export const UPDATE_SELECTION_CACHE = 'UPDATE_SELECTION_CACHE';
export const UPDATE_SELECTION = 'UPDATE_SELECTION';
export const UPDATE_SELECTION_ERROR = 'UPDATE_SELECTION_ERROR';
export const UPDATE_ADDITIONAL_INFORMATION = 'UPDATE_ADDITIONAL_INFORMATION';
export const UPDATE_BUDBEE = 'UPDATE_BUDBEE';
export const GET_ADDRESS = 'GET_ADDRESS';
export const UPDATE_ADDRESS = 'UPDATE_ADDRESS';
export const UPDATE_ERRORS = 'UPDATE_ERRORS';
export const DELIVERY_PROCEED = 'DELIVERY_PROCEED';
export const KLARNA_LOADED = 'KLARNA_LOADED';
export const KLARNA_UNLOADED = 'KLARNA_UNLOADED';
export const ADYEN_INITIATED = 'ADYEN_INITIATED';
export const STRIPE_INITIATED = 'STRIPE_INITIATED';
export const PAYMENT_UPDATING = 'PAYMENT_UPDATING';
export const SELECTION_ERROR = 'SELECTION_ERROR';
export const SELECTION_ERROR_STOCK = 'SELECTION_ERROR_STOCK';
export const ENRICH_PRODUCTS = 'ENRICH_PRODUCTS';
export const INITIATED_SCRIPT = 'INITIATED_SCRIPT';
export const BOOLEAN_CHANGE = 'BOOLEAN_CHANGE';
export const PAYMENT_RETURN = 'PAYMENT_RETURN';
export const DESTROY_SELECTION = 'DESTROY_SELECTION';
export const UPDATE_CHECKOUT_URL = 'UPDATE_CHECKOUT_URL';


export const clientInit = () => ({ type: 'CLIENT_INIT' });
export const retrieveProductCache = () => ({ type: 'RETRIEVE_PRODUCT_CACHE' });
export const selectSize = (product, item, quantity = 1) => ({ type: SELECT_SIZE, product, item, quantity });
export const addRecentlyViewed = (product) => ({ type: RECENTLY_VIEWED, product });

const removeInvalidPaymentFields = (setup, paymentData) => {
	const whitelistedFields = {
		address: ['address1', 'address2', 'city', 'company', 'country', 'firstName', 'lastName', 'phone', 'province', 'zip'],
	};

	// Copy only whitelisted fields.
	const returnData = Object.keys(paymentData).reduce((acc, f) => {
		// if (whitelistedFields.level0.indexOf(f) === -1) return acc;
		if (whitelistedFields[f]) {
			// check second level.
			return {
				...acc,
				[f]: Object.keys(paymentData[f]).reduce((acc1, f1) => {
					if (whitelistedFields[f].indexOf(f1) === -1) return acc1;
					return { ...acc1, [f1]: paymentData[f][f1] };
				}, {}),
			};
		}
		if (typeof paymentData[f] === 'object') return { ...acc, [f]: deepClone(paymentData[f]) };
		return { ...acc, [f]: paymentData[f] };
	}, {});

	// Add required fields
	returnData.address.country = returnData.address.country || setup.country;

	// Check that state is correct and applicable else remove.
	if (!(returnData.address.state && setup.countries?.[returnData.address.country]?.states?.[returnData.address.state])) delete returnData.address.state;

	return returnData;
};

export const subscribeMailChimp = ({ email, firstName, lastName, type, tags = [] }) => (dispatch, getState) => {
	const { country, language } = getState().environment.setup;

	let tag = 'WEBSITE SUBSCRIBERS';
	if (type === 'trade') tag = `${tag} PROFESSIONALS`;
	if (country === 'US') tag = `${tag} USA`;

	return (
		fetchData('mailchimp/subscribe', { email, firstName, lastName, tags: [tag, ...tags], country, language }).then((data) => {
			if (!data || data.errors) dispatch({ type: SUBSCRIBED_ERROR });
			return dispatch({ type: SUBSCRIBED });
		}).catch((err) => console.log(err))
	);
};

/* Create new checkout or add to checkout with email address. */
export const subscribeToNewsletter = ({ email, firstName, lastName, type, tags = [] }) => (dispatch, getState) => {
	const { market } = getState().environment.setup;
	const token = getState().checkout.selection.token;

	if (!token) {
		return dispatch(subscribeMailChimp({ email, firstName, lastName, type, tags })).then(() => dispatch(createCart({ email })));
	}

	const variables = {
		cartId: token,
		buyerIdentity: {
			email,
		},
		consent: getVisitorConsent(getState().environment.cookieConsent),
	};

	return dispatch(subscribeMailChimp({ email, firstName, lastName, type, tags })).then(() => (
		fetchCheckout('cartBuyerIdentityUpdate', market, variables)
	));
};

export const updateCartAttributes = ({ market, token, attributes, consent }) => {
	const variables = {
		cartId: token,
		attributes,
		consent,
	};

	return fetchCheckout('cartAttributesUpdate', market, variables).then((response) => response.data.cartAttributesUpdate.cart.id);
};

export const createCart = ({ email = false, lines = [], token = false, skipUpdate = false, addToBag = false }) => (dispatch, getState) => {
	const { country, market } = getState().environment.setup;
	const { items: itemsFromState = [], token: tokenFromState = false } = getState().checkout.selection;
	const consent = getVisitorConsent(getState().environment.cookieConsent);

	if (!token && tokenFromState) token = tokenFromState;

	if (token && !email && !skipUpdate) {
		const attributes = [...lines, ...itemsFromState]
			.filter((item) => item.attributes && item.attributes.length > 0)
			.reduce((acc, item) => ({
				...acc,
				...item.attributes.reduce((acc2, item2) => ({
					...acc2,
					[urlify(item2.key)]: item2,
				}), {}),
			}), {});

		return updateCartAttributes({ market, token, attributes: Object.keys(attributes).map((attribute) => attributes[attribute]), consent });
	}

	if (token && lines.length === 0) {
		return Promise.resolve(token);
	}

	const variables = {
		input: {
			buyerIdentity: {
				countryCode: country,
			},
			attributes: lines
				.filter((lineItem) => lineItem.attributes && lineItem.attributes.length > 0)
				.reduce((acc, lineItem) => [...acc, ...lineItem.attributes], []),
		},
		consent,
	};
	if (email) variables.input.email = email;
	if (lines.length > 0 && !addToBag) variables.input.lines = lines;

	return fetchCheckout('cartCreate', market, variables).then((response) => {
		dispatch({ type: UPDATE_SELECTION, data: response.data.cartCreate.cart });
		return response.data.cartCreate.cart.id;
	});
};

export const applyDiscountCode = ({ discountCode }) => (dispatch, getState) => {
	const { market } = getState().environment.setup;
	const consent = getVisitorConsent(getState().environment.cookieConsent);

	return (
		dispatch(createCart({ })).then((cartId) => fetchCheckout('cartDiscountCodesUpdate', market, { cartId, discountCodes: [discountCode], consent })).then((response) => {
			if (response?.data?.cartDiscountCodesUpdate?.userErrors?.length > 0) {
				return dispatch({ type: DISCOUNT_ERROR });
			}

			return dispatch({ type: UPDATE_SELECTION, data: response.data.cartDiscountCodesUpdate.cart });
		})
	);
};

export const updateCheckoutAddress = () => (dispatch, getState) => {
	const token = getState().checkout.selection.token;
	const { payment } = getState().checkout;
	const { setup } = getState().environment;
	const { countries, market } = setup;

	const cleanData = removeInvalidPaymentFields(setup, payment);

	const addressVariables = {
		checkoutId: token,
		shippingAddress: Object.keys(cleanData.address).reduce((acc, f) => {
			let value = cleanData.address[f];
			if (f === 'country') value = countries[value].name;
			return {
				...acc,
				[f]: value,
			};
		}, {}),
	};

	return fetchCheckout('checkoutShippingAddressUpdate', market, addressVariables).then((response) => {
		dispatch({ type: UPDATE_SELECTION, data: response.data.checkoutShippingAddressUpdateV2.checkout });
		const { checkoutUserErrors = [] } = response.data.checkoutShippingAddressUpdateV2;

		if (checkoutUserErrors.length > 0) {
			const errors = [];
			checkoutUserErrors.forEach((checkoutUserError) => {
				errors[errors.length] = {
					group: checkoutUserError.field[0],
					id: checkoutUserError.field[1],
					message: checkoutUserError.message,
				};
			});

			return new Promise((resolve, reject) => reject({ checkoutUserErrors: errors }));
		}

		return response.data.checkoutShippingAddressUpdateV2.checkout.id;
	}).then((response) => {
		const emailVariables = {
			checkoutId: response,
			email: getState().checkout.payment.address.email,
		};

		return fetchCheckout('checkoutEmailUpdate', market, emailVariables);
	});
};

export const addToBag = (lines = [], cachedItems, category) => (dispatch, getState) => {
	const { market } = getState().environment.setup;
	const consent = getVisitorConsent(getState().environment.cookieConsent);
	addClass(document.querySelector('html'), 'u_pageIsScrollingUp');

	return (
		dispatch(createCart({ lines, addToBag: 1 })).then((cartId) => fetchCheckout('cartLinesAdd', market, { cartId, lines, consent })).then((response) => {
			if (response.data.cartLinesAdd.userErrors.length > 0) return dispatch({ type: ADD_TO_BAG_ERROR });
			dispatch({ type: UPDATE_SELECTION_CACHE, lines, cachedItem: cachedItems, category });

			return dispatch({ type: UPDATE_SELECTION, data: response.data.cartLinesAdd.cart, addedToBag: true });
		}).catch((err) => console.log('DEBUG: err', err))
	);
};

export const changeQuantity = (lines = [], cachedItem, category) => (dispatch, getState) => {
	const { market } = getState().environment.setup;
	const consent = getVisitorConsent(getState().environment.cookieConsent);

	return dispatch(createCart({ })).then((cartId) => fetchCheckout('cartLinesUpdate', market, { cartId, lines, consent })).then((response) => {
		dispatch({ type: UPDATE_SELECTION_CACHE, lines, cachedItem, category });

		return dispatch({ type: UPDATE_SELECTION, data: response.data.cartLinesUpdate.cart });
	}).catch((err) => console.log('DEBUG: err', err));
};

/*
 * Re-fetches checkoutUrl for the current cart with fresh consent, since a URL only carries the
 * consent that was current when it was issued. Called whenever the visitor's cookie choice
 * changes (see cookieConsent in actions/environment.js) so a stale link isn't left in state.
 */
export const refreshCheckoutUrl = () => (dispatch, getState) => {
	const { market } = getState().environment.setup;
	const { token } = getState().checkout.selection;
	if (!token) return Promise.resolve();

	const consent = getVisitorConsent(getState().environment.cookieConsent);

	return fetchCheckout('cartCheckoutUrl', market, { cartId: token, consent }).then((response) => {
		const webUrl = response?.data?.cart?.checkoutUrl;
		return webUrl ? dispatch({ type: UPDATE_CHECKOUT_URL, webUrl }) : undefined;
	}).catch((err) => console.log('DEBUG: err', err));
};

export const updateCart = () => (dispatch, getState) => {
	const { items = [], token = false } = getState().checkout.selection;

	// If checkout never created then don't do anything.
	if (items.length === 0 && !token) return new Promise((resolve) => resolve());

	// Re-create a new basket with the current items.
	// TODO: Check that the items are available in current market.
	const lines = items.map((item) => ({ merchandiseId: item.id, quantity: item.quantity, attributes: item.attributes }));

	return dispatch(createCart({ lines, token: 1, skipUpdate: true }));
};

export const changeAddressInput = (group, id, value) => ({ type: UPDATE_ADDRESS, group, id, value });
export const booleanChange = (id) => ({ type: BOOLEAN_CHANGE, id });

export const checkAddressInputErrors = (group, id, value) => {
	const fieldType = id === 'email' ? 'email' : 'text';
	const error = fieldValidation(value, fieldType);

	return ({ type: UPDATE_ERRORS, group, id, error });
};

export const enrichProducts = (params) => ({ type: ENRICH_PRODUCTS, ...params });

export const destroySelection = () => (dispatch, getState) => {
	const { country, countries } = getState().environment.setup;
	const defaultCountrySettings = countries[country];
	// Clear possible campaign market/pricelist to keep in sync with Centra.
	dispatch({ type: 'UPDATE_COUNTRY', country, market: parseInt(defaultCountrySettings.market, 10), pricelist: parseInt(defaultCountrySettings.pricelist, 10) });
	dispatch({ type: 'RETRIEVE_PRODUCT_CACHE' });

	dispatch({ type: DESTROY_SELECTION });
};
